import {
  MdAdd,
  MdCheck,
  MdClose,
  MdOutlineTipsAndUpdates,
} from "react-icons/md";
import Modal from "../Modal/Modal";
import { useTranslation } from "react-i18next";
import { ICustomSelectOption } from "../Select/CustomSelect";
import clsx from "clsx";
import {
  KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import InfoBlock from "../InfoBlock/InfoBlock";
import RadioGrid from "../RadioGroup/RadioGrid";
import useMobile from "src/hooks/useMobile";
import CheckBoxGrid from "../CheckBoxGroup/CheckBoxGrid";
import TableQuickFilter from "../TableQuickFilter/TableQuickFilter";
import { usePagination } from "src/hooks/usePagination";
import CardsPagination from "../Cards/CardsPagination";
import NoTableData from "../NoTableData/NoTableData";
import { ICON_SIZES } from "src/constants/icons";
import { EButtonKind } from "../Button/Button.types";

const MOBILE_PAGE_SIZE = 6;
const DESKTOP_PAGE_SIZE = 14;

interface ISelectModal {
  onClose: () => void;
  options: ICustomSelectOption[];
  isMulti?: boolean;
  onSelect: (e: any) => void;
  value: any;
  title?: string | undefined;
  description?: string;
  defaultSearchValue?: string;
  allowCreateOption?: boolean;
}

const normalize = (text: string) =>
  text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const SelectModal: React.FC<ISelectModal> = ({
  onClose,
  options,
  isMulti = false,
  value,
  onSelect,
  title = undefined,
  description,
  defaultSearchValue = "",
  allowCreateOption = false,
}) => {
  const { t } = useTranslation(["modals", "common"]);
  const [newSelectedValue, setNewSelectedValue] = useState<any>(
    isMulti ? value : value?.value,
  );
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [filteredResults, setFilteredResults] =
    useState<ICustomSelectOption[]>(options);

  const { isMobile } = useMobile();

  const filterResults = useCallback(() => {
    const term = normalize(searchTerm || "");

    if (term) {
      setFilteredResults(
        options?.filter?.((option: ICustomSelectOption) => {
          const label = normalize(`${option?.label || ""}`);
          const value = normalize(`${option?.value || ""}`);
          return label.includes(term) || value.includes(term);
        }),
      );
    } else {
      setFilteredResults(options);
    }
  }, [searchTerm, options]);

  const {
    items,
    next,
    prev,
    canNext,
    canPrev,
    goTo,
    page,
    totalPages,
    showPagination,
  } = usePagination(filteredResults, {
    pageSize: isMobile ? MOBILE_PAGE_SIZE : DESKTOP_PAGE_SIZE,
  });

  useEffect(() => {
    if (!defaultSearchValue || !filteredResults?.length) return;

    const activeIndex = filteredResults.findIndex(
      (item) => item.value === defaultSearchValue,
    );

    if (activeIndex === -1) return;

    const targetPage =
      Math.floor(
        activeIndex / (isMobile ? MOBILE_PAGE_SIZE : DESKTOP_PAGE_SIZE),
      ) + 1;

    if (page !== targetPage && targetPage <= totalPages) {
      goTo(targetPage);
    }
  }, [defaultSearchValue, filterResults, isMobile, goTo, totalPages]);

  useEffect(() => {
    filterResults();
  }, [searchTerm]);

  const selectedCountMessage = useMemo(() => {
    if (isMulti) {
      const total = (newSelectedValue as any[])?.length;

      return t("modals:select_modal.selected", { count: total });
    }

    return null;
  }, [newSelectedValue]);

  return (
    <Modal
      onEscape={() => onClose()}
      className="md:!max-w-[680px] md:!min-w-[280px] md:w-full"
    >
      <Modal.Header
        onClose={() => onClose()}
        title={
          isMulti
            ? t("modals:select_modal.title", { count: 2 })
            : t("modals:select_modal.title", { count: 1 })
        }
        // title={
        //   isMulti
        //     ? title || t("modals:select_modal.title", { count: 2 })
        //     : title || t("modals:select_modal.title", { count: 1 })
        // }
      />
      <Modal.Body>
        <div className={clsx(["flex", "w-full", "flex-col", "gap-4"])}>
          {options?.length ? (
            <div className={clsx(["flex", "gap-4", "flex-col", "w-full"])}>
              {description && (
                <InfoBlock
                  icon={<MdOutlineTipsAndUpdates />}
                  message={description}
                  displayMobileIcon
                />
              )}
              <TableQuickFilter
                onChange={setSearchTerm}
                searchTerm={searchTerm}
                onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
                  if (e?.key === "Enter") {
                    onSelect(
                      isMulti
                        ? {
                            value: searchTerm.trim(),
                            label: searchTerm.trim(),
                          }
                        : searchTerm.trim(),
                    );
                  }
                }}
                buttons={
                  allowCreateOption
                    ? [
                        {
                          disabled: !searchTerm?.trim?.(),
                          icon: <MdAdd />,
                          tooltip: t("common:buttons.add"),
                          onClick: () => {
                            onSelect(
                              isMulti
                                ? {
                                    value: searchTerm.trim(),
                                    label: searchTerm.trim(),
                                  }
                                : searchTerm.trim(),
                            );
                          },
                        },
                      ]
                    : []
                }
              />

              {items?.length ? (
                <>
                  {isMulti ? (
                    <CheckBoxGrid
                      columns={isMobile ? 1 : 2}
                      onChange={(e: any) => {
                        setNewSelectedValue(e);
                      }}
                      options={items}
                      selected={newSelectedValue as any[]}
                      onSwipeLeft={showPagination ? next : undefined}
                      onSwipeRight={showPagination ? prev : undefined}
                    />
                  ) : (
                    <RadioGrid
                      columns={isMobile ? 1 : 2}
                      onChange={(value: any) => {
                        if (isMulti) {
                          setNewSelectedValue(value);
                        } else {
                          if (value === newSelectedValue) {
                            setNewSelectedValue("");
                          } else {
                            setNewSelectedValue(value);
                          }
                        }
                      }}
                      options={items}
                      selected={newSelectedValue as string}
                      onSwipeLeft={showPagination ? next : undefined}
                      onSwipeRight={showPagination ? prev : undefined}
                    />
                  )}
                </>
              ) : (
                <NoTableData
                  text={
                    allowCreateOption
                      ? t("modals:select_modal.no_results_add_new_message")
                      : t("modals:select_modal.no_results_message")
                  }
                  buttons={
                    allowCreateOption
                      ? [
                          {
                            text: t("common:tooltips.add"),
                            onClick: () =>
                              onSelect(
                                isMulti
                                  ? {
                                      value: searchTerm.trim(),
                                      label: searchTerm.trim(),
                                    }
                                  : searchTerm.trim(),
                              ),
                            kind: EButtonKind.NORMAL,
                            icon: <MdAdd />,
                          },
                        ]
                      : []
                  }
                />
              )}
            </div>
          ) : (
            <InfoBlock
              message={`No hay elementos previamente guardados. Debes agregar manualmente ${
                isMulti ? "los valores" : "el valor"
              } del campo.`}
              title="Lista vacía"
              displayMobileIcon
            />
          )}
        </div>
      </Modal.Body>
      <Modal.Footer
        actions={[
          {
            icon: <MdCheck size={ICON_SIZES.SM} />,
            onClick: () => onSelect(newSelectedValue),
            kind: EButtonKind.SAVE,
            text: t("common:buttons.accept"),
            // disabled: !newSelectedValue,
            hide: options?.length === 0,
          },
          {
            text: t("common:buttons.close"),
            onClick: () => onClose(),
            kind: EButtonKind.NORMAL,
            icon: <MdClose size={ICON_SIZES.SM} />,
            hide: isMobile,
          },
        ]}
        leftContent={
          items?.length ? (
            <CardsPagination
              canNext={canNext}
              canPrev={canPrev}
              goTo={goTo}
              next={next}
              prev={prev}
              page={page}
              totalPages={totalPages}
              // hidePageCount={isMobile}
              hideFirstPageButton={isMobile}
              hideLastPageButton={isMobile}
              pageSize={isMobile ? MOBILE_PAGE_SIZE : DESKTOP_PAGE_SIZE}
              totalItems={filteredResults?.length}
            />
          ) : null
        }
      />
    </Modal>
  );
};

export default SelectModal;
